import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getAttributionData, getCouponHint, clearCouponHint } from '../utils/affiliateStorage';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

interface PostSignupResult {
  success: boolean;
  message?: string;
  granted_credits?: number;
  error?: string;
}

export const usePostSignupActions = () => {
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Handle post-signup actions like coupon redemption
   * Call this immediately after successful signup or first login
   */
  const handlePostSignupActions = async (userId: string): Promise<PostSignupResult> => {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    setIsProcessing(true);

    try {
      // Check if there's a coupon hint stored from a referral link
      const couponHint = getCouponHint();
      
      if (!couponHint) {
        // No coupon to redeem, but that's ok
        return { success: true, message: 'Account created successfully!' };
      }

      // Get attribution data (affiliate info if any)
      const { affiliate_data } = getAttributionData();

      // Redeem the coupon automatically
      const response = await fetch(`${API_URL}/api/auth/post-signup-redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          coupon_code: couponHint.code,
          affiliate_data,
          idempotency_key: uuidv4()
        })
      });

      const result = await response.json();

      if (response.ok) {
        // Clear the coupon hint since it was successfully used
        clearCouponHint();
        
        return {
          success: true,
          message: result.message || `Welcome! You received ${result.granted_credits || 0} bonus credits.`,
          granted_credits: result.granted_credits
        };
      } else {
        // Coupon redemption failed, but signup was successful
        console.error('Post-signup coupon redemption failed:', result.error);
        
        // Don't fail the entire signup process just because coupon failed
        // Just log it and continue
        return {
          success: true,
          message: 'Account created successfully! (Welcome bonus could not be applied)',
          error: result.error
        };
      }
    } catch (error) {
      console.error('Post-signup actions error:', error);
      
      // Don't fail signup because of post-signup action errors
      return {
        success: true,
        message: 'Account created successfully!',
        error: 'Failed to process welcome bonus'
      };
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    handlePostSignupActions,
    isProcessing
  };
};